import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, of, from } from 'rxjs';
import {
    tap,
    catchError,
    switchMap,
    map,
    concatMap,
    reduce,
} from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LoggerService } from '../core/services/logger.service';
import {
    LiquidacionAgenciaFilterDto,
    LiquidacionAgenciaResponseDto,
    ILiquidacionAgencia,
    ILiquidacionAgenciaFacturacionRow,
} from '@opb-workspace/shared';
import { chunkIds } from './liquidaciones-ids-chunk.util';
import {
    mapV1ToNormalized as mapV1ToNormalizedUtil,
    transformV2ToNormalized as transformV2ToNormalizedUtil,
    getEmptyNormalizedData as getEmptyNormalizedDataUtil,
} from './liquidaciones-excel-normalize.util';

import {
    TemporadaDto,
    LiquidacionVdFilter,
    LiquidacionVd,
    LiquidacionVdResponse,
    ValorDeclaradoResponse,
    DetalleSeguroResponse,
    LiquidacionAgenciaFilter,
    LiquidacionAgencia,
    LiquidacionAgenciaResponse,
    LiquidacionFilter,
    Liquidacion,
    LiquidacionResponse,
    ApiResponse,
    ExcelV2Header,
    ExcelV2AgenciamientoRow,
    ExcelV2TransporteRow,
    ExcelV2Response,
    ExcelDataNormalized,
    LiquidacionAgenciaContenedoresTabResponse,
    LiquidacionAgenciaTotalesTabResponse,
    LiquidacionAgenciaExtraServiciosTabResponse,
    ExtraRows,
} from './models/liquidaciones.models';
export * from './models/liquidaciones.models';

@Injectable({
    providedIn: 'root',
})
export class LiquidacionesService {
    private apiUrl = `${environment.apiUrl}/v1`;

    constructor(private http: HttpClient, private logger: LoggerService) {}

    // ============================================================================
    // MÉTODOS PRINCIPALES LIQUIDACIONES VD
    // ============================================================================

    /**
     * Obtener lista de liquidaciones VD con filtros
     */
    getLiquidacionesVd(
        filters: LiquidacionVdFilter = {}
    ): Observable<LiquidacionVdResponse> {
        this.logger.info(
            'Obteniendo liquidaciones VD con filtros',
            'LiquidacionesService'
        );

        let params = new HttpParams();

        if (filters.clienteId) {
            params = params.set('clienteId', filters.clienteId.toString());
        }
        if (filters.temporada) {
            params = params.set('temporada', filters.temporada.toString());
        }
        // Corregir nombres de parámetros de fecha para que coincidan con el backend
        if (filters.fechaDesde) {
            params = params.set('dateFrom', filters.fechaDesde);
        }
        if (filters.fechaHasta) {
            params = params.set('dateTo', filters.fechaHasta);
        }
        if (filters.valorMinimo) {
            params = params.set('valorMinimo', filters.valorMinimo.toString());
        }
        if (filters.valorMaximo) {
            params = params.set('valorMaximo', filters.valorMaximo.toString());
        }
        if (filters.tipoEnvio) {
            params = params.set('tipoEnvio', filters.tipoEnvio);
        }
        if (filters.estado) {
            params = params.set('estado', filters.estado);
        }
        if (filters.coberturaSeguro) {
            params = params.set('coberturaSeguro', filters.coberturaSeguro);
        }
        if (filters.page) {
            params = params.set('page', filters.page.toString());
        }
        if (filters.limit) {
            params = params.set('limit', filters.limit.toString());
        }
        if (filters.orderBy) {
            params = params.set('orderBy', filters.orderBy);
        }
        if (filters.orderDirection) {
            params = params.set('orderDirection', filters.orderDirection);
        }

        return this.http.get<LiquidacionVdResponse>(
            `${this.apiUrl}/liquidaciones-vd`,
            { params, headers: this.getAuthHeaders() }
        );
    }

    /**
     * Obtener una liquidación VD específica por ID
     */
    getLiquidacionVdById(id: number): Observable<any> {
        this.logger.info(
            `Obteniendo liquidación VD con ID: ${id}`,
            'LiquidacionesService'
        );
        return this.http.get(`${this.apiUrl}/liquidaciones-vd/${id}`, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener datos para generar PDF de una liquidación VD
     */
    getLiquidacionVdPdfData(id: number): Observable<any> {
        this.logger.info(
            `Obteniendo datos PDF para liquidación VD ID: ${id}`,
            'LiquidacionesService'
        );
        return this.http.get(`${this.apiUrl}/liquidaciones-vd/${id}/pdf-data`, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener PDF de liquidación VD directamente del backend
     */
    getLiquidacionVdPdf(id: number): Observable<Blob> {
        this.logger.info(
            `Obteniendo PDF para liquidación VD ID: ${id}`,
            'LiquidacionesService'
        );
        return this.http.get(`${this.apiUrl}/liquidaciones-vd/${id}/pdf`, {
            responseType: 'blob',
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener datos para generar Excel de una liquidación VD
     */
    getLiquidacionVdExcelData(
        id: number,
        options?: {
            estado?: string;
            orderBy?: string;
            orderDirection?: 'ASC' | 'DESC';
        }
    ): Observable<any> {
        this.logger.info(
            `Obteniendo datos Excel para liquidación VD ID: ${id}`,
            'LiquidacionesService'
        );

        let params = new HttpParams();
        if (options?.estado) {
            params = params.set('estado', options.estado);
        }
        if (options?.orderBy) {
            params = params.set('orderBy', options.orderBy);
        }
        if (options?.orderDirection) {
            params = params.set('orderDirection', options.orderDirection);
        }

        return this.http.get(
            `${this.apiUrl}/liquidaciones-vd/${id}/excel-data`,
            { params, headers: this.getAuthHeaders() }
        );
    }

    /**
     * Calcular valor declarado de una liquidación VD
     */
    getValorDeclarado(id: number): Observable<ValorDeclaradoResponse> {
        this.logger.info(
            `Calculando valor declarado para liquidación VD ID: ${id}`,
            'LiquidacionesService'
        );
        return this.http.get<ValorDeclaradoResponse>(
            `${this.apiUrl}/liquidaciones-vd/${id}/valor-declarado`,
            { headers: this.getAuthHeaders() }
        );
    }

    /**
     * Obtener detalle de seguro de una liquidación VD
     */
    getDetalleSeguro(id: number): Observable<DetalleSeguroResponse> {
        this.logger.info(
            `Obteniendo detalle de seguro para liquidación VD ID: ${id}`,
            'LiquidacionesService'
        );
        return this.http.get<DetalleSeguroResponse>(
            `${this.apiUrl}/liquidaciones-vd/${id}/detalle-seguro`,
            { headers: this.getAuthHeaders() }
        );
    }

    // ============================================================================
    // MÉTODOS DE COMPATIBILIDAD (para mantener código existente funcionando)
    // ============================================================================

    /**
     * Obtener lista de liquidaciones con filtros (método de compatibilidad)
     * @deprecated Usar getLiquidacionesVd() para nuevas implementaciones
     */
    getLiquidaciones(
        filters: LiquidacionFilter = {}
    ): Observable<LiquidacionResponse> {
        this.logger.info(
            '⚠️ Usando método de compatibilidad getLiquidaciones - considera migrar a getLiquidacionesVd',
            'LiquidacionesService'
        );
        return this.getLiquidacionesVd(filters);
    }

    /**
     * Obtener una liquidación específica por ID (método de compatibilidad)
     * @deprecated Usar getLiquidacionVdById() para nuevas implementaciones
     */
    getLiquidacionById(id: number): Observable<any> {
        this.logger.info(
            '⚠️ Usando método de compatibilidad getLiquidacionById - considera migrar a getLiquidacionVdById',
            'LiquidacionesService'
        );
        return this.getLiquidacionVdById(id);
    }

    /**
     * Obtener datos para generar PDF de una liquidación (método de compatibilidad)
     * @deprecated Usar getLiquidacionVdPdfData() para nuevas implementaciones
     */
    getLiquidacionPdfData(id: number): Observable<any> {
        this.logger.info(
            '⚠️ Usando método de compatibilidad getLiquidacionPdfData - considera migrar a getLiquidacionVdPdfData',
            'LiquidacionesService'
        );
        return this.getLiquidacionVdPdfData(id);
    }

    /**
     * Analizar datos de una liquidación VD
     */
    analyzeLiquidacionVdData(id: number): Observable<any> {
        this.logger.info(
            `📊 [SERVICE] Analizando datos de liquidación VD ID: ${id}`,
            'LiquidacionesService'
        );
        return this.http
            .get<any>(`${this.apiUrl}/liquidaciones-vd/${id}/pdf-data`, {
                headers: this.getAuthHeaders(),
            })
            .pipe(
                tap((response) => {
                    this.logger.info(
                        `✅ [SERVICE] Análisis completado para liquidación VD ID: ${id}`,
                        'LiquidacionesService'
                    );
                }),
                catchError((error: any) => {
                    this.logger.error(
                        `❌ [SERVICE] Error analizando liquidación VD ID: ${id}: ${
                            error.message || 'Error desconocido'
                        }`,
                        error as Error,
                        'LiquidacionesService'
                    );
                    throw error;
                })
            );
    }

    /**
     * Analizar datos de una liquidación (método de compatibilidad)
     * @deprecated Usar analyzeLiquidacionVdData() para nuevas implementaciones
     */
    analyzeLiquidacionData(id: number): Observable<any> {
        this.logger.info(
            '⚠️ Usando método de compatibilidad analyzeLiquidacionData - considera migrar a analyzeLiquidacionVdData',
            'LiquidacionesService'
        );
        return this.analyzeLiquidacionVdData(id);
    }

    // ==============================================
    // MÉTODOS LIQUIDACIONES AGENCIA
    // ==============================================

    /**
     * Obtener lista de liquidaciones de agencia con filtros
     */
    getLiquidacionesAgencia(
        filters: LiquidacionAgenciaFilter = {}
    ): Observable<LiquidacionAgenciaResponse> {
        this.logger.info(
            '🚀 [SERVICE] Obteniendo liquidaciones de agencia con filtros',
            'LiquidacionesService'
        );
        this.logger.info(
            `📋 [SERVICE] Filtros recibidos: ${JSON.stringify(filters)}`,
            'LiquidacionesService'
        );
        this.logger.info(
            `🔍 [SERVICE] clienteId en filtros: ${
                filters.clienteId || 'UNDEFINED'
            } (tipo: ${typeof filters.clienteId})`,
            'LiquidacionesService'
        );

        const params: any = {};

        // Filtros básicos de paginación
        if (filters.page) params.page = filters.page.toString();
        if (filters.limit) params.limit = filters.limit.toString();
        if (filters.orderBy) params.orderBy = filters.orderBy;
        if (filters.orderDirection)
            params.orderDirection = filters.orderDirection;

        // Filtros específicos de agencia
        if (filters.mesAnio) params.mesAnio = filters.mesAnio;
        if (filters.estado) params.estado = filters.estado;
        if (filters.tipoServicio) params.tipoServicio = filters.tipoServicio;
        if (filters.cliente) params.cliente = filters.cliente;
        if (filters.clienteId) {
            params.clienteId = filters.clienteId.toString();
            this.logger.info(
                `🔍 [SERVICE] clienteId convertido a string: ${params.clienteId}`,
                'LiquidacionesService'
            );
        }
        if (filters.temporada) params.temporada = filters.temporada;
        if (filters.comisionMinima !== undefined)
            params.comisionMinima = filters.comisionMinima.toString();
        if (filters.comisionMaxima !== undefined)
            params.comisionMaxima = filters.comisionMaxima.toString();
        if (filters.fechaDesde) params.dateFrom = filters.fechaDesde;
        if (filters.fechaHasta) params.dateTo = filters.fechaHasta;

        // Punto A.1 (OPB, ago-2026). Se envían sólo si tienen valor: un string vacío
        // llegaría al backend como filtro activo y devolvería el conjunto vacío.
        if (filters.tipoId) params.tipoId = filters.tipoId.toString();
        if (filters.nroLiquidacion?.trim())
            params.nroLiquidacion = filters.nroLiquidacion.trim();
        if (filters.nroFactura?.trim())
            params.nroFactura = filters.nroFactura.trim();
        if (filters.nroNotaCobro?.trim())
            params.nroNotaCobro = filters.nroNotaCobro.trim();
        if (filters.estadoId) params.estadoId = filters.estadoId.toString();

        this.logger.info(
            `🌐 [SERVICE] URL: ${this.apiUrl}/liquidaciones-agencia`,
            'LiquidacionesService'
        );
        this.logger.info(
            `📤 [SERVICE] Parámetros enviados: ${JSON.stringify(params)}`,
            'LiquidacionesService'
        );

        return this.http
            .get<LiquidacionAgenciaResponse>(
                `${this.apiUrl}/liquidaciones-agencia`,
                { params, headers: this.getAuthHeaders() }
            )
            .pipe(
                tap((response) => {
                    this.logger.info(
                        `✅ [SERVICE] Respuesta recibida del backend`,
                        'LiquidacionesService'
                    );
                    this.logger.info(
                        `📊 [SERVICE] Total items: ${
                            response.items?.length || 0
                        }`,
                        'LiquidacionesService'
                    );
                    this.logger.info(
                        `📊 [SERVICE] Total count: ${response.total}`,
                        'LiquidacionesService'
                    );
                    this.logger.info(
                        `📊 [SERVICE] Resumen presente: ${
                            'resumen' in response
                        }`,
                        'LiquidacionesService'
                    );
                    if ('resumen' in response && response.resumen) {
                        this.logger.info(
                            `📊 [SERVICE] Resumen recibido: ${JSON.stringify(
                                response.resumen
                            )}`,
                            'LiquidacionesService'
                        );
                    } else {
                        this.logger.warn(
                            `⚠️ [SERVICE] No se recibió resumen en la respuesta`,
                            'LiquidacionesService'
                        );
                    }
                    if (response.items && response.items.length > 0) {
                        this.logger.info(
                            `📋 [SERVICE] Primer item: ${JSON.stringify(
                                response.items[0]
                            )}`,
                            'LiquidacionesService'
                        );
                    }
                }),
                catchError((error: any) => {
                    this.logger.error(
                        `❌ [SERVICE] Error en petición HTTP: ${
                            error.message || 'Error desconocido'
                        }`,
                        error,
                        'LiquidacionesService'
                    );
                    this.logger.error(
                        `❌ [SERVICE] Error completo: ${JSON.stringify(error)}`,
                        error,
                        'LiquidacionesService'
                    );
                    throw error;
                })
            );
    }

    /**
     * Obtener liquidación de agencia por ID
     */
    getLiquidacionAgenciaById(id: number): Observable<any> {
        this.logger.info(
            `Obteniendo liquidación de agencia con ID: ${id}`,
            'LiquidacionesService'
        );
        return this.http.get(`${this.apiUrl}/liquidaciones-agencia/${id}`, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener datos para PDF de liquidación de agencia
     */
    getLiquidacionAgenciaPdfData(id: number): Observable<any> {
        this.logger.info(
            `Obteniendo datos PDF para liquidación de agencia ID: ${id}`,
            'LiquidacionesService'
        );
        return this.http.get(
            `${this.apiUrl}/liquidaciones-agencia/${id}/pdf-data`,
            { headers: this.getAuthHeaders() }
        );
    }

    /**
     * Obtener datos para Excel de liquidación de agencia individual (v1)
     * @deprecated Usar getLiquidacionAgenciaExcelDataByVersion() para nuevas implementaciones
     */
    getLiquidacionAgenciaExcelData(id: number): Observable<any> {
        this.logger.info(
            `Obteniendo datos Excel V1 para liquidación de agencia ID: ${id}`,
            'LiquidacionesService'
        );
        return this.http.get(
            `${this.apiUrl}/liquidaciones-agencia/${id}/excel-data`,
            { headers: this.getAuthHeaders() }
        );
    }

    /**
     * Obtener datos para Excel de liquidación de agencia individual (v2)
     * Nueva estructura con agenciamiento y transportes
     */
    getLiquidacionAgenciaExcelDataV2(id: number): Observable<any> {
        this.logger.info(
            `Obteniendo datos Excel V2 para liquidación de agencia ID: ${id}`,
            'LiquidacionesService'
        );
        return this.http.get(
            `${environment.apiUrl}/v2/liquidaciones-agencia/${id}/excel-data`,
            { headers: this.getAuthHeaders() }
        );
    }

    /**
     * Obtener datos para Excel según la versión configurada
     * @param id ID de la liquidación
     * @param version Versión del endpoint ('v1' | 'v2'), por defecto 'v1'
     */
    getLiquidacionAgenciaExcelDataByVersion(
        id: number,
        version: 'v1' | 'v2' = 'v1'
    ): Observable<any> {
        this.logger.info(
            `Obteniendo datos Excel ${version.toUpperCase()} para liquidación de agencia ID: ${id}`,
            'LiquidacionesService'
        );

        if (version === 'v2') {
            return this.getLiquidacionAgenciaExcelDataV2(id);
        }
        return this.getLiquidacionAgenciaExcelData(id);
    }

    /**
     * Datos específicos del TAB "Contenedores" en el detalle de Liquidación Agencia.
     * Usa endpoint dedicado para no afectar excel-data/pdf-data.
     */
    getLiquidacionAgenciaContenedoresTab(id: number): Observable<{
        success: boolean;
        data: LiquidacionAgenciaContenedoresTabResponse;
    }> {
        return this.http.get<{
            success: boolean;
            data: LiquidacionAgenciaContenedoresTabResponse;
        }>(`${this.apiUrl}/liquidaciones-agencia/${id}/contenedores-tab`, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Datos específicos del TAB "Pallets" en el detalle de Liquidación Agencia.
     * Usa endpoint dedicado con SP que agrupa por Nave+POL+ETD.
     */
    getLiquidacionAgenciaPalletsTab(id: number): Observable<{
        success: boolean;
        data: LiquidacionAgenciaContenedoresTabResponse;
    }> {
        return this.http.get<{
            success: boolean;
            data: LiquidacionAgenciaContenedoresTabResponse;
        }>(`${this.apiUrl}/liquidaciones-agencia/${id}/pallets-tab`, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Tab Totales del detalle: filas por nave + fila Totales (SP en SQL Server).
     */
    getLiquidacionAgenciaTotalesTab(id: number): Observable<{
        success: boolean;
        data: LiquidacionAgenciaTotalesTabResponse;
    }> {
        return this.http.get<{
            success: boolean;
            data: LiquidacionAgenciaTotalesTabResponse;
        }>(`${this.apiUrl}/liquidaciones-agencia/${id}/totales-tab`, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Tab Extra Servicios del detalle: órdenes con TypeOfShipmentId IS NULL (SP).
     */
    getLiquidacionAgenciaExtraServiciosTab(id: number): Observable<{
        success: boolean;
        data: LiquidacionAgenciaExtraServiciosTabResponse;
    }> {
        return this.http.get<{
            success: boolean;
            data: LiquidacionAgenciaExtraServiciosTabResponse;
        }>(`${this.apiUrl}/liquidaciones-agencia/${id}/extra-servicios-tab`, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener datos Excel normalizados (compatible con frontend existente)
     * Mapea automáticamente v2 a estructura v1 si es necesario
     * @param id ID de la liquidación
     * @param version Versión del endpoint ('v1' | 'v2')
     */
    getLiquidacionAgenciaExcelDataNormalized(
        id: number,
        version: 'v1' | 'v2' = 'v1',
        preFetchedExtraServiciosRows?: ExtraRows,
        preFetchedExtraServiciosFailed = false
    ): Observable<{
        data: ExcelDataNormalized;
        extraServiciosFailed: boolean;
    }> {
        this.logger.info(
            `Obteniendo datos Excel normalizados (${version}) para liquidación ID: ${id}`,
            'LiquidacionesService'
        );

        if (version === 'v2') {
            return this.getLiquidacionAgenciaExcelDataV2(id).pipe(
                tap(() => {
                    this.logger.info(
                        'Respuesta V2 recibida, mapeando a estructura normalizada',
                        'LiquidacionesService'
                    );
                }),
                catchError((error) => {
                    this.logger.error(
                        `Error en V2: ${error.message}`,
                        error,
                        'LiquidacionesService'
                    );
                    throw error;
                }),
                // MEJ-01 (D7) fix N+1: filas pre-obtenidas por el Consolidado
                // (`getExtraServiciosTabForIds`) se inyectan sin llamada extra.
                switchMap((response) => {
                    if (preFetchedExtraServiciosRows !== undefined) {
                        return of({
                            data: this.transformV2ToNormalized(
                                response,
                                preFetchedExtraServiciosRows,
                                preFetchedExtraServiciosFailed
                            ),
                            extraServiciosFailed:
                                preFetchedExtraServiciosFailed,
                        });
                    }
                    return this.getExtraServiciosTabBatch([id]).pipe(
                        map((extraResponse) => ({
                            data: this.transformV2ToNormalized(
                                response,
                                extraResponse?.data?.items?.[0]?.data?.rows ??
                                    []
                            ),
                            extraServiciosFailed: false,
                        })),
                        // RESILIENCIA (OPB 2026-10-01 / PR #368 + 2026-10-03): un
                        // 404/500/timeout acá NO debe tirar abajo todo el Excel —
                        // solo afecta la hoja "Servicios Extra", que muestra un
                        // aviso explícito en vez de quedar vacía en silencio. El
                        // resto de las hojas (Agenciamiento, Transportes, etc.)
                        // se genera igual.
                        catchError((error) => {
                            this.logger.error(
                                `No se pudo obtener Extra-Servicios (batch) para liquidación ${id}`,
                                error,
                                'LiquidacionesService'
                            );
                            return of({
                                data: this.transformV2ToNormalized(
                                    response,
                                    [],
                                    true
                                ),
                                extraServiciosFailed: true,
                            });
                        })
                    );
                })
            );
        }

        // V1: ya tiene estructura compatible, solo normalizar
        return this.getLiquidacionAgenciaExcelData(id).pipe(
            tap((response) => {
                this.logger.info(
                    'Respuesta V1 recibida',
                    'LiquidacionesService'
                );
            }),
            this.mapV1ToNormalized()
        );
    }

    /** MEJ-01 (D7): batch de Extra-Servicios (SP corrido secuencial por id, cap 200). Single la llama con `ids=[id]`; Consolidado usa `getExtraServiciosTabForIds`. */
    getExtraServiciosTabBatch(ids: number[]): Observable<{
        success: boolean;
        data: {
            items: Array<{
                id: number;
                data: LiquidacionAgenciaExtraServiciosTabResponse;
            }>;
        };
    }> {
        return this.http.get<{
            success: boolean;
            data: {
                items: Array<{
                    id: number;
                    data: LiquidacionAgenciaExtraServiciosTabResponse;
                }>;
            };
        }>(`${this.apiUrl}/liquidaciones-agencia/extra-servicios-tab`, {
            params: { ids: ids.join(',') },
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * MEJ-01 (D7) fix N+1, extendido para el Excel Facturación >200
     * seleccionadas (OPB, 2026-10-01): el backend rechaza con 400 cualquier
     * llamada de más de 200 ids, así que acá se parte en bloques SECUENCIALES
     * de a lo sumo 200 (`chunkIds`) en vez de mandar todos los ids de una.
     *
     * 🔴 A propósito SIN `catchError`: si un bloque falla, el error se
     * PROPAGA — nunca se silencia devolviendo un Map vacío. Antes, una falla
     * acá dejaba seguir el export con las hojas de Extra-Servicios vacías
     * para TODAS las liquidaciones, sin avisar. El llamador (Consolidado en
     * el componente) ya tiene su propio `catchError` que muestra el
     * snackbar de error y aborta el export — ver
     * `liquidaciones-agencia.component.ts` → `generarExcelFusionado`.
     */
    getExtraServiciosTabForIds(
        ids: number[]
    ): Observable<Map<number, ExtraRows>> {
        const chunks = chunkIds(ids);
        if (chunks.length === 0) {
            return of(new Map<number, ExtraRows>());
        }
        return from(chunks).pipe(
            concatMap((chunk) => this.getExtraServiciosTabBatch(chunk)),
            reduce((rowsById, response) => {
                (response?.data?.items || []).forEach((i) =>
                    rowsById.set(i.id, i.data?.rows || [])
                );
                return rowsById;
            }, new Map<number, ExtraRows>())
        );
    }

    /**
     * MEJ-03b: Excel Facturación (`POST /liquidaciones-agencia/facturacion`,
     * D11). El backend exige `ids` O `filtros`, nunca los dos ni ninguno —
     * ver `LiquidacionAgenciaFacturacionService.resolveFilters`. El rol se
     * valida en el backend (`@Roles('admin','operator')`); acá sólo se oculta
     * el botón.
     */
    facturarExcel(
        body: { ids: number[] } | { filtros: LiquidacionAgenciaFilterDto }
    ): Observable<{ rows: ILiquidacionAgenciaFacturacionRow[] }> {
        return this.http.post<{ rows: ILiquidacionAgenciaFacturacionRow[] }>(
            `${this.apiUrl}/liquidaciones-agencia/facturacion`,
            body,
            { headers: this.getAuthHeaders() }
        );
    }

    /**
     * Operador RxJS para normalizar respuesta V1
     */
    private mapV1ToNormalized() {
        return mapV1ToNormalizedUtil();
    }

    /**
     * Transforma respuesta V2 a estructura normalizada compatible con frontend
     */
    private transformV2ToNormalized(
        response: ExcelV2Response,
        extraServiciosRows: Record<string, string | number | null>[] = [],
        extraServiciosFailed = false
    ): ExcelDataNormalized {
        return transformV2ToNormalizedUtil(
            response,
            extraServiciosRows,
            extraServiciosFailed,
            this.logger
        );
    }

    /**
     * Retorna estructura normalizada vacía
     */
    private getEmptyNormalizedData(): ExcelDataNormalized {
        return getEmptyNormalizedDataUtil();
    }

    /**
     * Obtener PDF de liquidación de agencia directamente del backend
     */
    getLiquidacionAgenciaPdf(id: number): Observable<Blob> {
        this.logger.info(
            `Obteniendo PDF para liquidación de agencia ID: ${id}`,
            'LiquidacionesService'
        );
        return this.http.get(`${this.apiUrl}/liquidaciones-agencia/${id}/pdf`, {
            responseType: 'blob',
        });
    }

    /**
     * Generar Excel de liquidación de agencia
     */
    generarExcelLiquidacionAgencia(id: number): Observable<any> {
        this.logger.info(
            `Generando Excel para liquidación de agencia ID: ${id}`,
            'LiquidacionesService'
        );
        return this.http.get(
            `${this.apiUrl}/liquidaciones-agencia/${id}/excel`,
            { responseType: 'blob' }
        );
    }

    /**
     * Generar PDF de liquidación de agencia
     */
    generarPdfLiquidacionAgencia(id: number): Observable<Blob> {
        this.logger.info(
            `Generando PDF para liquidación de agencia ID: ${id}`,
            'LiquidacionesService'
        );
        const token = this.getAuthToken();
        const pdfHeaders = new HttpHeaders({
            Accept: 'application/pdf',
            Authorization: token ? `Bearer ${token}` : '',
        });
        return this.http.get(`${this.apiUrl}/liquidaciones-agencia/${id}/pdf`, {
            responseType: 'blob',
            headers: pdfHeaders,
        });
    }

    /**
     * Obtener token de autenticación del localStorage
     */
    private getAuthToken(): string | null {
        return localStorage.getItem('access_token');
    }

    /**
     * Crear headers con token de autenticación
     */
    private getAuthHeaders(): { [key: string]: string } {
        const token = this.getAuthToken();
        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
    }

    // =====================================================
    // MÉTODOS ORDENES DE SERVICIO AGENCIA
    // =====================================================

    /**
     * Obtener documentos PDF asociados a una orden de servicio
     * @param serviceOrderId ID de la orden de servicio
     */
    getDocumentosPdfOrdenServicio(serviceOrderId: number): Observable<any> {
        this.logger.info(
            `Obteniendo documentos PDF para orden de servicio ID: ${serviceOrderId}`,
            'LiquidacionesService'
        );
        return this.http.get(
            `${this.apiUrl}/os-agencias/files/${serviceOrderId}`
        );
    }

    /**
     * Descargar todos los documentos PDF de una orden de servicio en un solo archivo
     * @param serviceOrderId ID de la orden de servicio
     */
    descargarTodosDocumentosPdf(serviceOrderId: number): Observable<Blob> {
        this.logger.info(
            `Descargando todos los documentos PDF para orden de servicio ID: ${serviceOrderId}`,
            'LiquidacionesService'
        );
        return this.http.get(
            `${this.apiUrl}/os-agencias/files/${serviceOrderId}/merge-download`,
            {
                responseType: 'blob',
                headers: new HttpHeaders({
                    Accept: 'application/pdf',
                }),
            }
        );
    }

    /**
     * Descargar un documento PDF específico de una orden de servicio
     * @param serviceOrderId ID de la orden de servicio
     * @param fileName Nombre del archivo a descargar
     */
    descargarDocumentoPdf(
        serviceOrderId: number,
        fileName: string
    ): Observable<Blob> {
        this.logger.info(
            `Descargando documento PDF ${fileName} de la orden de servicio ID: ${serviceOrderId}`,
            'LiquidacionesService'
        );
        return this.http.get(
            `${this.apiUrl}/os-agencias/files/${serviceOrderId}/download/${fileName}`,
            {
                responseType: 'blob',
                headers: new HttpHeaders({
                    Accept: 'application/pdf',
                    'Content-Disposition': 'attachment',
                }),
            }
        );
    }

    // =====================================================
    // MÉTODOS COMMONS - TEMPORADAS
    // =====================================================

    /**
     * Obtener todas las temporadas activas
     * Endpoint público que no requiere autenticación
     */
    getTemporadas(): Observable<TemporadaDto[]> {
        this.logger.info(
            'Obteniendo temporadas desde el backend',
            'LiquidacionesService'
        );

        const url = `${this.apiUrl}/commons/temporadas`;

        return this.http.get<TemporadaDto[]>(url);
    }
}
